import React, { useEffect, useState } from "react";
import { useReportAutoRun } from "hooks/useReportAutoRun/useReportAutoRun";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "hooks/redux";

import { downloadCsv, formatDate, openPrintWindow } from "helpers/Helpers";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";
import { showError, showSuccess } from "redux/actions/ui";

import { GetReport } from "api/rpc/2024-04/facilityAdmin/report/report";
import { GetRegisters } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import CashReconciliationTable from "../tables/CashReconciliationTable";
import FilterDropdown from "components/filterDropDown/FilterDropDown";
import { useTranslation } from "react-i18next";
import { IRegister } from "../../Admin";

import "../report.scss";
import ReportEmailModal from "components/reportEmailModal/ReportEmailModal";
import moment from "moment";
import { useReportRun } from "../useReportRun";

interface IReportState {
  id: number;
  title: string;
  code: string;
  dateRangeStart: Date;
  dateRangeEnd: Date;
  singleDate: Date;
  date_range: Array<string>;
  date: string;
  facilityOptions: Array<{ label: string; onClick: () => void }>;
  facility_id_name: string;
  register_ids: number[];
  sales_channels: number[];
  email: string
  [key: string]: any;
}

interface IReportDataState {
  headings: any;
  rows: any;
  columns: Array<Record<string, any>>;
  tableDisplay: boolean;
  description: string;
  compileDate: string;
}

export default function CashReconciliationWorksheet() {
  const windowSize = useWindowSize();
  const location = useLocation();
  const { t } = useTranslation();
  const routeName = location.pathname.split("/");
  const code = routeName[routeName.length - 1];
  const dispatch = useAppDispatch();
  const GetReportRun = useReportRun();

  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:59"];
  }

  const today = new Date();

  const [state, setState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    configurations: [],
    date_range: formatDateRange(today, today),
    date: formatDate(today),
    facilityOptions: [],
    facility_id_name: "",
    register_ids: [],
    sales_channels: [],
    email: "",
  });

  const [reportData, setReportData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
  });

  const [registers, setRegisters] = useState<IRegister[]>([]);

  useEffect(() => {
    void loadReport();
    void loadRegisters();
  }, []);
  useReportAutoRun({
    isReady: state.id != null,
    reportId: state.id,
    setState,
    run: () => {
      void getReport();
    },
  });


  async function loadReport() {
    const reportRes = await GetReport({ code: code }, true);
    if (reportRes.status !== StatusCode.OK) {
      return;
    }

    const report = reportRes.data[0];

    setState(prev => ({
      ...prev,
      id: report.id,
      title: report.title,
      code: report.code,
    }));
  }

  async function loadRegisters() {
    const registerRes = await GetRegisters(null, true);

    if (registerRes.status !== StatusCode.OK) {
      return;
    }

    setRegisters(registerRes.data);
  }

  async function getReport() {
    const reportRes = await GetReportRun(
      {
        report_id: state.id,
        register_ids: state.register_ids,
        date_range: state.date_range,
        sales_channels: state.sales_channels,
      },
      true,
    );

    if (reportRes.status !== StatusCode.OK) {
      dispatch(showError(reportRes.message));
      return;
    }

    if (reportRes.message) {
      dispatch(showSuccess(reportRes.message));
    }

    const compileDate = reportRes.data?.compiled_at ? reportRes.data?.compiled_at.concat("Z") : "";

    setReportData(prev => ({
      ...prev,
      rows: reportRes.data.rows,
      columns: reportRes.data.columns,
      description: reportRes.data?.description ?? "",
      compileDate,
    }));

    return;
  }

  async function getDownload(email: string) {

    const resp = await GetReportRun(
      {
        report_id: state.id,
        type: "csv",
        register_ids: state.register_ids,
        date_range: state.date_range,
        sales_channels: state.sales_channels,
        email: email,
      },
      true,
    );

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.deposits.001")));
      return;
    }

    if (!email) {
      downloadCsv(resp.data as string, reportFilename() + '.csv');
    } else {
      dispatch(showSuccess(resp.message));
    }
  }

  async function getPrint(email: string) {
    const printRes = await GetReportRun(
      {
        report_id: state.id,
        type: "print",
        register_ids: state.register_ids,
        date_range: state.date_range,
        sales_channels: state.sales_channels,
        email: email,
      },
      true,
    );

    if (printRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.deposits.002")));
      return;
    }

    if (!email) {
      openPrintWindow(printRes.data as string);
    } else {
      dispatch(showSuccess(printRes.message));
    }
  }

  useEffect(() => {
    if (state.dateRangeStart && state.dateRangeEnd) {
      const date_range = formatDateRange(state.dateRangeStart, state.dateRangeEnd);
      setState(prevState => ({ ...prevState, date_range }));
    }
  }, [state.dateRangeStart, state.dateRangeEnd]);

  function onCheckBoxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value, checked, id } = e.target;
    let updateSelectedValues = [];
    if (state[id]) {
      updateSelectedValues = [...state[id]];
    }
    if (checked) {
      updateSelectedValues.push(value);
    } else {
      const removeIndex = updateSelectedValues?.findIndex(valueId => valueId === value);
      if (removeIndex !== -1) {
        updateSelectedValues.splice(removeIndex, 1);
      }
    }
    setState(prevState => ({ ...prevState, [id]: updateSelectedValues }));
  }

  function handleRemoveAll(id: string) {
    setState(prevState => ({ ...prevState, [id]: [] }));
  }

  function handleAddAllRegisters(id: string) {
    const updatedSelectedRegisters: number[] = [];
    registers?.forEach(register => {
      updatedSelectedRegisters.push(register?.id);
    });
    setState(prevState => ({
      ...prevState,
      [id]: updatedSelectedRegisters,
    }));
  }

  const [reportEmail, setReportEmail] = useState({
    handler: null,
    open: false,
  });

  function handleReportEmailSelected(email: string) {
    setState({ ...state, email });
    setReportEmail({
      ...reportEmail,
      open: false,
    });
  }

  function handleReportEmailClose() {
    setReportEmail({ handler: null, open: false });
  }

  useEffect(() => {
    if (reportEmail.open === false && reportEmail.handler !== null) {
      reportEmail.handler(state.email);
    }
  }, [reportEmail.open]);

  const primaryAction = {
    content: t("secure.facility.reports.pages.deposits.003"),
    action: getReport,
  };

  function reportFilename(): string {
    return `${state.title}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase();

  }
  function reportShouldQueue(): boolean {
    return moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), 'days') >= state.queue_after_days;
  }

  function handleDownload() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getDownload,
      });
    } else {
      void getDownload('');
    }
  }

  function handlePrint() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getPrint,
      });
    } else {
      void getPrint('');
    }
  }

  useEffect(() => {
    if (state.dateRangeStart && state.dateRangeEnd) {
      const date_range = formatDateRange(state.dateRangeStart, state.dateRangeEnd);
      setState(prevState => ({ ...prevState, date_range }));
    }
  }, [state.dateRangeStart, state.dateRangeEnd]);

  function setDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  function onSalesChannelCheckBoxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value, checked } = event.target;
    setState(prevState => {
      const selected = [...prevState.sales_channels];
      if (checked) {
        if (!selected.includes(value)) {
          selected.push(value);
        }
      } else {
        const removeIndex = selected.indexOf(value);
        if (removeIndex !== -1) {
          selected.splice(removeIndex, 1);
        }
      }
      return { ...prevState, sales_channels: selected };
    });
  }

  function handleSelectAllSalesChannels() {
    setState(prevState => ({
      ...prevState,
      sales_channels: ["pos", "online_store", "booking_engine"],
    }));
  }

  return (
    <Page
      title={t("Cash Reconciliation Worksheet Report")}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("secure.facility.reports.pages.deposits.005"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("secure.facility.reports.pages.deposits.006"),
              handler: handleDownload,
              icon: "file-arrow-down",
            },
            {
              type: "handler",
              label: t("secure.facility.reports.pages.deposits.007"),
              handler: handlePrint,
              icon: "print",
            },
          ],
        },
      }}
      breadcrumbs={[{ prefix: true, label: t("secure.facility.reports.pages.deposits.008"), url: "/admin/report" }]}
    >
      <Card>
        <Card.Section>
          <div className="report-filters-container">
            <DatePickerInput
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.dateRangeStart}
              setStartingDate={setDateRangeStart}
              endingDate={state.dateRangeEnd}
              setEndingDate={setDateRangeEnd}
            />
            <FilterDropdown
              label={t("Registers")}
              data={[{ id: "null", title: t("No Register") }, ...(registers ?? [])]}
              dataLabelProperty={"title"}
              checkBoxId={"register_ids"}
              dataValueProperty="id"
              onCheckBoxChange={onCheckBoxChange}
              onClear={() => handleRemoveAll("register_ids")}
              onAddAll={() => handleAddAllRegisters("register_ids")}
              leftAligned={windowSize.width <= MOBILE_WIDTH}
            />
            <FilterDropdown
              label={t("Sales Channels")}
              data={[
                { id: "pos", title: t("Point of Sale") },
                { id: "online_store", title: t("Online Store") },
                { id: "booking_engine", title: t("Online Booking") },
              ]}
              dataLabelProperty={"title"}
              checkBoxId={"sales_channels"}
              dataValueProperty="id"
              onCheckBoxChange={onSalesChannelCheckBoxChange}
              onClear={() => handleRemoveAll("sales_channels")}
              onAddAll={handleSelectAllSalesChannels}
              leftAligned={windowSize.width <= MOBILE_WIDTH}
            />
          </div>
        </Card.Section>
      </Card>

      <div className="dynamic-table-container">
        <CashReconciliationTable
          data={reportData.rows}
          columns={reportData.columns}
          description={reportData.description}
          compileDate={reportData.compileDate}
        />
      </div>
      <ReportEmailModal
        open={reportEmail.open}
        onEmailSelected={handleReportEmailSelected}
        onClose={handleReportEmailClose}
      />
    </Page>
  );
}
